import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Onboarding() {
  const { login, signup } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState("age_gate");
  const [mode, setMode] = useState("login");
  const [accepted, setAccepted] = useState({});
  const [form, setForm] = useState({ email: "", password: "", username: "" });
  const [adultConsent, setAdultConsent] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const CHARTER_RULES = [
    { id: "consent", icon: "◈", title: t("charter_rule_consent_title"), description: t("charter_rule_consent_desc") },
    { id: "discretion", icon: "🔒", title: t("charter_rule_discretion_title"), description: t("charter_rule_discretion_desc") },
    { id: "respect", icon: "✦", title: t("charter_rule_respect_title"), description: t("charter_rule_respect_desc") },
  ];

  const allAccepted = CHARTER_RULES.every((r) => accepted[r.id]);
  function toggleRule(id) { setAccepted((prev) => ({ ...prev, [id]: !prev[id] })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSuccess(""); setSubmitting(true);
    try {
      if (mode === "signup") {
        if (!adultConsent) { setError("Tu dois confirmer avoir 18 ans et accepter le caractère adulte du site."); return; }
        await signup(form.email, form.password, form.username, true);
        setSuccess(t("auth_signup_success"));
        setMode("login");
        setForm((f) => ({ ...f, password: "" }));
        setAdultConsent(false);
      } else {
        await login(form.email, form.password);
      }
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-sensoria-bg px-4">
      {/* Ambient effects */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-purple-800/20 blur-[140px]" />
        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-rose-900/15 blur-[120px]" />
        <div className="absolute -bottom-20 -right-40 h-[400px] w-[400px] rounded-full bg-sensoria-accent/10 blur-[100px]" />
      </div>

      {/* Language switcher */}
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center animate-fade-in">
          <img src="/logo.png" alt="Univers Libertin" className="h-44 w-auto drop-shadow-2xl sm:h-52" />
        </div>

        {/* ─── Age Gate ─── */}
        {step === "age_gate" && (
          <div className="animate-slide-up">
            <div className="sensoria-card sensoria-glow text-center">
              <div className="mb-4 text-5xl">🔞</div>
              <h2 className="mb-2 font-display text-xl font-bold text-sensoria-text">{t("age_gate_title")}</h2>
              <p className="mb-6 text-sm leading-relaxed text-sensoria-text-dim">{t("age_gate_desc")}</p>
              <div className="space-y-3">
                <button onClick={() => setStep("charter")} className="sensoria-btn w-full">
                  {t("age_gate_confirm")}
                </button>
                <button onClick={() => window.history.back()}
                  className="w-full rounded-xl border border-sensoria-border px-6 py-3 text-sm font-medium text-sensoria-muted transition-colors hover:border-sensoria-accent/40 hover:text-sensoria-text">
                  {t("age_gate_deny")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Charter ─── */}
        {step === "charter" && (
          <div className="animate-slide-up">
            <div className="sensoria-card sensoria-glow">
              <h2 className="mb-1 font-display text-lg font-semibold text-sensoria-text">{t("charter_title")}</h2>
              <p className="mb-5 text-sm text-sensoria-text-dim">{t("charter_desc")}</p>
              <div className="space-y-2.5">
                {CHARTER_RULES.map((rule, i) => (
                  <button key={rule.id} type="button" onClick={() => toggleRule(rule.id)} className="group w-full text-left"
                    style={{ animationDelay: `${i * 80}ms` }}>
                    <div className={`flex gap-3 rounded-xl border p-3.5 transition-all duration-300 ${
                      accepted[rule.id]
                        ? "border-sensoria-accent/50 bg-sensoria-accent/10"
                        : "border-sensoria-border bg-sensoria-surface hover:border-sensoria-muted"
                    }`}>
                      <div className="mt-0.5 flex-shrink-0">
                        <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all duration-300 ${
                          accepted[rule.id]
                            ? "border-sensoria-accent bg-sensoria-accent"
                            : "border-sensoria-border group-hover:border-sensoria-muted"
                        }`}>
                          {accepted[rule.id] && (
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sensoria-accent text-xs">{rule.icon}</span>
                          <span className="font-semibold text-sensoria-text text-sm">{rule.title}</span>
                        </div>
                        <p className="mt-0.5 text-[11px] leading-relaxed text-sensoria-text-dim">{rule.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => allAccepted && setStep("auth")} disabled={!allAccepted} className="sensoria-btn mt-5 w-full">
                {allAccepted ? t("charter_accept_all") : t("charter_accept_pending")}
              </button>
            </div>
          </div>
        )}

        {/* ─── Auth ─── */}
        {step === "auth" && (
          <div className="animate-slide-up">
            <div className="sensoria-card sensoria-glow">
              {/* Tabs */}
              <div className="mb-5 flex rounded-xl bg-sensoria-surface p-1">
                {["login", "signup"].map((m) => (
                  <button key={m} onClick={() => { setMode(m); setError(""); setSuccess(""); }}
                    className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-all duration-300 ${
                      mode === m
                        ? "bg-gradient-to-r from-sensoria-accent to-purple-600 text-white shadow-lg shadow-sensoria-glow"
                        : "text-sensoria-muted hover:text-sensoria-text"
                    }`}>
                    {m === "login" ? t("auth_login") : t("auth_signup")}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                {mode === "signup" && (
                  <div>
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-sensoria-muted">{t("auth_pseudo")}</label>
                    <input type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                      placeholder={t("auth_pseudo_placeholder")} className="sensoria-input" required minLength={2} maxLength={30} />
                    <p className="mt-0.5 text-[10px] text-sensoria-muted">{t("auth_pseudo_hint")}</p>
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-sensoria-muted">{t("auth_email")}</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t("auth_email_placeholder")} className="sensoria-input" required autoComplete="email" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-sensoria-muted">{t("auth_password")}</label>
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={t("auth_password_placeholder")} className="sensoria-input" required minLength={8}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"} />
                </div>
                {mode === "signup" && (
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-sensoria-border/40 bg-sensoria-surface/50 p-3 transition-all hover:border-sensoria-accent/30">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={adultConsent}
                        onChange={e => setAdultConsent(e.target.checked)}
                      />
                      <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all ${
                        adultConsent
                          ? "border-sensoria-accent bg-sensoria-accent"
                          : "border-sensoria-border"
                      }`}>
                        {adultConsent && (
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] leading-relaxed text-sensoria-text-dim">
                      Je certifie avoir <strong className="text-sensoria-text">18 ans ou plus</strong> et j'accepte
                      que ce site est réservé à un public adulte à connotation libertine.
                      J'ai lu et j'accepte la{" "}
                      <Link to="/p/charte-communautaire" className="text-sensoria-accent hover:underline" target="_blank">charte communautaire</Link>
                      {" "}et les{" "}
                      <Link to="/p/conditions-generales-utilisation" className="text-sensoria-accent hover:underline" target="_blank">CGU</Link>.
                    </span>
                  </label>
                )}
                {error && <div className="rounded-xl border border-sensoria-danger/30 bg-sensoria-danger/10 px-4 py-2.5 text-sm text-sensoria-danger">{error}</div>}
                {success && <div className="rounded-xl border border-sensoria-success/30 bg-sensoria-success/10 px-4 py-2.5 text-sm text-sensoria-success">{success}</div>}
                <button type="submit" disabled={submitting || (mode === "signup" && !adultConsent)} className="sensoria-btn w-full disabled:opacity-50">
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      {mode === "login" ? t("auth_logging_in") : t("auth_signing_up")}
                    </span>
                  ) : mode === "login" ? t("auth_login_btn") : t("auth_signup_btn")}
                </button>
              </form>
              <button onClick={() => setStep("charter")}
                className="mt-3 block w-full text-center text-xs text-sensoria-muted hover:text-sensoria-accent transition-colors">
                {t("charter_read_again")}
              </button>
            </div>
          </div>
        )}

        {/* Footer links */}
        <p className="mt-6 text-center text-[11px] text-sensoria-muted">{t("footer_trust")}</p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-sensoria-muted">
          <Link to="/p/a-propos" className="hover:text-sensoria-accent transition-colors">À propos</Link>
          <span className="text-sensoria-border">·</span>
          <Link to="/p/comment-ca-marche" className="hover:text-sensoria-accent transition-colors">Comment ça marche</Link>
          <span className="text-sensoria-border">·</span>
          <Link to="/p/tarifs" className="hover:text-sensoria-accent transition-colors">Tarifs</Link>
          <span className="text-sensoria-border">·</span>
          <Link to="/p/contact" className="hover:text-sensoria-accent transition-colors">Contact</Link>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[10px] text-sensoria-border">
          <Link to="/p/mentions-legales" className="hover:text-sensoria-muted transition-colors">Mentions légales</Link>
          <span>·</span>
          <Link to="/p/politique-de-confidentialite" className="hover:text-sensoria-muted transition-colors">Confidentialité</Link>
          <span>·</span>
          <Link to="/p/conditions-generales-utilisation" className="hover:text-sensoria-muted transition-colors">CGU</Link>
          <span>·</span>
          <Link to="/p/charte-communautaire" className="hover:text-sensoria-muted transition-colors">Charte</Link>
        </div>
        <p className="mt-4 text-center text-[9px] text-sensoria-border">© {new Date().getFullYear()} Univers Libertin. Tous droits réservés.</p>
      </div>
    </div>
  );
}
